/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useRef, useCallback, useContext, useMemo } from "react";
import axios from "axios";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import Rating from "@mui/material/Rating";
import { useTheme, useMediaQuery, FormControl, FormLabel, Paper, CircularProgress } from "@mui/material";
import { useAuth } from "react-oidc-context";
import { useMutation } from "@tanstack/react-query";
import AppContext from './AppContext';

// --- IMPORTS for Export Feature ---
import Fab from '@mui/material/Fab';
import Tooltip from '@mui/material/Tooltip';
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from '@mui/material/IconButton';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import LinearProgress from '@mui/material/LinearProgress';
import IosShareIcon from '@mui/icons-material/IosShare';
import CloseIcon from '@mui/icons-material/Close';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import CheckIcon from '@mui/icons-material/Check';
import SendIcon from '@mui/icons-material/Send';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import InputBase from '@mui/material/InputBase';
import Popover from '@mui/material/Popover';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
// ---

import config from './config.json';
import "./styles.css";

// --- Interfaces ---
interface ChatPromptProps {
  scoreThreshold: number;
  historyRetention: number;
  listTextChunksFlag: boolean;
  currentSubject: string;
  subjectDisplayName?: string;
  selectedDocument: string;
  question: string;
  autoSendTrigger: number;
  isAnyDialogOpen: boolean;
  filterLabel?: string;
  filterPlaceholder?: string;
  filterOptions?: string[];
  filterValue?: string | null;
  onFilterChange?: (value: string | null) => void;
}
interface UserQuestion { text: string; index: number; }
interface Vote { index: number; rating: number; feedback: string; question: string; answer: string; scoreThreshold: number; historyRetention: number; questionCategory: string; embeddedQuestion: string; subject?: string; userEmail?: string; cognitoUsername?: string; cognitoGroups?: string[]; }
interface ChatMessage { text: string; isBot: boolean; chartImage?: string; questionCategory?: string; embeddedQuestion?: string; }


// --- API Functions ---
const postChatMessage = async (payload: any, auth: any) => {
  const userApiKey = auth.user?.profile?.['custom:api_key'] as string;
  if (!auth.user?.id_token || !userApiKey) {
    throw new Error("Authentication error: API key or token is missing.");
  }
  const baseChatbotUrl = config.api.chatbot.url.endsWith('/') ? config.api.chatbot.url.slice(0, -1) : config.api.chatbot.url;
  const chatbotUrl = `${baseChatbotUrl}/complete`;

  console.log(payload);

  const response = await axios.post(chatbotUrl, payload, {
    headers: { 'Authorization': auth.user?.id_token, 'X-Api-Key': userApiKey },
    timeout: config.timeouts.chatApi,
  });
  return response.data;
};

const postFeedback = async (payload: Vote, auth: any) => {
  const userApiKey = auth.user?.profile?.['custom:api_key'] as string;
  if (!auth.user?.id_token || !userApiKey) {
    throw new Error("Authentication details are missing.");
  }
  const baseChatbotUrl = config.api.chatbot.url.endsWith('/') ? config.api.chatbot.url.slice(0, -1) : config.api.chatbot.url;
  const feedbackUrl = `${baseChatbotUrl}/feedback`;

  const response = await axios.post(feedbackUrl, payload, {
    headers: { 'Authorization': auth.user?.id_token, 'X-Api-Key': userApiKey },
    timeout: config.timeouts.chatApi,
  });
  return response.data;
};


// Wrapper: the context guard must live OUTSIDE the component that calls the
// remaining hooks — an early return before hooks violates rules-of-hooks and
// fails the production build.
const ChatPrompt: React.FC<ChatPromptProps> = (props) => {
  const appContext = useContext(AppContext);
  if (!appContext) {
    return <Typography>Error: Chat context not available.</Typography>;
  }
  return <ChatPromptInner {...props} appContext={appContext} />;
};

const ChatPromptInner: React.FC<ChatPromptProps & { appContext: NonNullable<React.ContextType<typeof AppContext>> }> = ({ scoreThreshold, historyRetention, listTextChunksFlag, currentSubject, subjectDisplayName, selectedDocument, question, autoSendTrigger, isAnyDialogOpen, filterLabel, filterPlaceholder, filterOptions, filterValue, onFilterChange, appContext }) => {
  const auth = useAuth();

  const {
    chatMessages, setChatMessages,
    chatHistory, setChatHistory,
    submittedFeedback, setSubmittedFeedback
  } = appContext;

  const [prompt, setPrompt] = useState("");
  const [votes, setVotes] = useState<Vote[]>([]);
  const [userQuestions, setUserQuestions] = useState<UserQuestion[]>([]);
  const [openFeedbackDialog, setOpenFeedbackDialog] = useState(false);
  const [currentFeedbackIndex, setCurrentFeedbackIndex] = useState<number | null>(null);
  const [currentRating, setCurrentRating] = useState<number | null>(null);
  const [currentFeedbackText, setCurrentFeedbackText] = useState("");
  
  // --- STATE for Export Feature ---
  const [isExportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportScope, setExportScope] = useState<'all' | 'select'>('all');
  const [selectedPairs, setSelectedPairs] = useState<number[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [showPulse, setShowPulse] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  
  const messagesEndRef = useRef<null | HTMLDivElement>(null);
  const prevAutoSendTriggerRef = useRef<number>(autoSendTrigger);
  const lastAutoSentValueRef = useRef<string | null>(null);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // Effect for the one-time pulse animation on the export button
  useEffect(() => {
    const hasSeenPulse = localStorage.getItem('hasSeenExportPulse');
    if (!hasSeenPulse && chatMessages.length > 0) {
        setShowPulse(true);
        localStorage.setItem('hasSeenExportPulse', 'true');
        const timer = setTimeout(() => setShowPulse(false), 2500);
        return () => clearTimeout(timer);
    }
  }, [chatMessages.length]);

  useEffect(() => {
    if (question && question.trim() !== "") {
      if (question !== prompt && question !== lastAutoSentValueRef.current) {
        setPrompt(question);
        lastAutoSentValueRef.current = null;
      }
    }
  }, [question, prompt]); 

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages]);

  const { mutate: sendMessageMutation, isPending: isLoading, isError: apiError } = useMutation({
    mutationFn: (textToSend: string) => {
        const requestPayload = {
            prompt: textToSend, scoreThreshold, historyRetention, listTextChunksFlag,
            history: chatHistory, 
            subject: currentSubject,
            selectedDocument: selectedDocument,
            userEmail: auth.user?.profile?.email as string | undefined,
            cognitoUsername: (auth.user?.profile?.["cognito:username"] as string | undefined) || (auth.user?.profile?.preferred_username as string | undefined),
            cognitoGroups: auth.user?.profile?.["cognito:groups"] as string[] | undefined,
        };
        return postChatMessage(requestPayload, auth);
    },
    onSuccess: (result) => {
        const botReplyText = String(result.response);
        const botReply: ChatMessage = {
            text: botReplyText, isBot: true, chartImage: result.chartImage || undefined,
            questionCategory: result.question_category || undefined, embeddedQuestion: result.embedded_question || undefined,
        };
        setChatMessages((prevMessages) => [...prevMessages, botReply]);
        setChatHistory(result.history || []);
    },
  });

  const { mutate: submitFeedbackMutation, isPending: isSubmittingFeedback, isSuccess: isFeedbackSuccess, isError: isFeedbackError } = useMutation({
    mutationFn: (newVote: Vote) => postFeedback(newVote, auth),
    onSuccess: (_, variables) => {
        setSubmittedFeedback((prev) => [...prev, variables.index]);
        setVotes((prevVotes) => [...prevVotes, variables]);
    },
    onSettled: () => {
        handleCloseFeedbackDialog();
    }
  });

  const handleOpenFeedbackDialog = (index: number, presetRating?: number) => {
    setCurrentFeedbackIndex(index);
    if (presetRating !== undefined) {
      setCurrentRating(presetRating);
    }
    setOpenFeedbackDialog(true);
  };

  const handleCopyMessage = async (index: number, text: string) => {
    const plainText = text.replace(/<[^>]*>/g, '');
    try {
      await navigator.clipboard.writeText(plainText);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = plainText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex((cur: number | null) => (cur === index ? null : cur)), 1500);
  };

  const handleCloseFeedbackDialog = () => {
    setOpenFeedbackDialog(false);
    setCurrentRating(null);
    setCurrentFeedbackText("");
    setCurrentFeedbackIndex(null);
  };

  const handleSubmitFeedback = () => {
    if (currentFeedbackIndex !== null && currentRating !== null) {
      const userMessageIndex = currentFeedbackIndex - 1;
      const botMessageIndex = currentFeedbackIndex;
      const newVote: Vote = {
        index: currentFeedbackIndex, rating: currentRating, feedback: currentFeedbackText,
        question: chatMessages[userMessageIndex]?.text || "",
        answer: chatMessages[botMessageIndex]?.text || "",
        questionCategory: chatMessages[botMessageIndex]?.questionCategory || "",
        embeddedQuestion: chatMessages[botMessageIndex]?.embeddedQuestion || "",
        scoreThreshold, historyRetention, subject: currentSubject,
        userEmail: auth.user?.profile?.email,
        cognitoUsername: (auth.user?.profile?.["cognito:username"] as string | undefined),
        cognitoGroups: auth.user?.profile?.["cognito:groups"] as string[] | undefined,
      };
      submitFeedbackMutation(newVote);
    }
  };

   const sendMessage = useCallback((textToSendOverride?: string) => {
     const currentText = textToSendOverride || prompt;
     if (!currentText || !currentText.trim() || isLoading) return;
     const userMessage: ChatMessage = { text: currentText, isBot: false };
     setChatMessages((prevMessages) => [...prevMessages, userMessage]);
     setPrompt(""); 
     if (textToSendOverride) { lastAutoSentValueRef.current = currentText; }
     setUserQuestions((prevQuestions) => [...prevQuestions, { text: currentText, index: chatMessages.length + 1 }]);
     sendMessageMutation(currentText);
   }, [prompt, isLoading, sendMessageMutation, chatMessages.length, setChatMessages]);

   useEffect(() => {
     const hasTriggeredThisCycle = autoSendTrigger > prevAutoSendTriggerRef.current;
     if (hasTriggeredThisCycle) {
       prevAutoSendTriggerRef.current = autoSendTrigger;
     }
     if (hasTriggeredThisCycle && !isLoading) {
       if (question && question.trim() !== "") {
         if (prompt !== question) {
           setPrompt(question);
         }
         sendMessage(question);
       }
     }
   }, [autoSendTrigger, isLoading, question, prompt, sendMessage, setPrompt]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setPrompt(e.target.value);
    };

    const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey && !isLoading) {
            e.preventDefault();
            sendMessage();
        }
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!isLoading) {
            sendMessage();
        }
    };

    // --- HANDLERS and LOGIC for Export Feature ---
    const qaPairsForSelection = useMemo(() => {
        const pairs: { questionIndex: number; question: string; answer: string }[] = [];
        chatMessages.forEach((message, index) => {
            if (!message.isBot && index + 1 < chatMessages.length) {
                const nextMessage = chatMessages[index + 1];
                if (nextMessage.isBot) {
                    pairs.push({
                        questionIndex: index,
                        question: message.text,
                        answer: nextMessage.text,
                    });
                }
            }
        });
        return pairs;
    }, [chatMessages]);

    const handleOpenExportDialog = () => setExportDialogOpen(true);

    const handleCloseExportDialog = () => {
        if (isExporting) return;
        setExportDialogOpen(false);
        setExportScope('all');
        setSelectedPairs([]);
    };

    const handleTogglePairSelection = (questionIndex: number) => {
        setSelectedPairs((prev) =>
            prev.includes(questionIndex)
                ? prev.filter((id) => id !== questionIndex)
                : [...prev, questionIndex]
        );
    };
    
    const handleToggleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelectedPairs(qaPairsForSelection.map(p => p.questionIndex));
        } else {
            setSelectedPairs([]);
        }
    };
    
    const areAllPairsSelected = qaPairsForSelection.length > 0 && selectedPairs.length === qaPairsForSelection.length;
    const areSomePairsSelected = selectedPairs.length > 0 && !areAllPairsSelected;

    const handleExport = () => {
        const qaPairsToExport = qaPairsForSelection
            .filter(pair => exportScope === 'all' || selectedPairs.includes(pair.questionIndex))
            .map(({ question, answer }) => ({ question, answer }));
    
        const sanitizeCsvField = (text: string): string => {
            // Strip any HTML tags
            let cleanText = text.replace(/<[^>]*>/g, '');
            // Escape any double quotes by doubling them
            const escapedText = cleanText.replace(/"/g, '""');
            // Enclose the entire field in double quotes to handle newlines, semicolons, etc.
            return `"${escapedText}"`;
        };
    
        const headers = "question;answer";
        const csvRows = qaPairsToExport.map(pair => {
            const cleanQuestion = sanitizeCsvField(pair.question);
            const cleanAnswer = sanitizeCsvField(pair.answer);
            return `${cleanQuestion};${cleanAnswer}`; // Standard CSV row, no trailing semicolon
        });
    
        const csvContent = [headers, ...csvRows].join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'conversation-export.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        handleCloseExportDialog();
    };

    const showExportButton = !isAnyDialogOpen && !openFeedbackDialog && qaPairsForSelection.length > 0;

  const markdownComponents: any = {
      p: ({ children }: any) => (
          <Typography component="p" variant="body2" sx={{ fontSize: isSmallScreen ? '0.9rem' : '0.95rem', lineHeight: 1.65, my: 0.75, color: 'text.primary', wordBreak: 'break-word' }}>
              {children}
          </Typography>
      ),
      ul: ({ children }: any) => (
          <Box component="ul" sx={{ pl: 3, my: 0.75, '& li': { mb: 0.25 } }}>{children}</Box>
      ),
      ol: ({ children }: any) => (
          <Box component="ol" sx={{ pl: 3, my: 0.75, '& li': { mb: 0.25 } }}>{children}</Box>
      ),
      li: ({ children }: any) => (
          <Typography component="li" variant="body2" sx={{ fontSize: isSmallScreen ? '0.9rem' : '0.95rem', lineHeight: 1.6 }}>{children}</Typography>
      ),
      strong: ({ children }: any) => <Box component="strong" sx={{ fontWeight: 600 }}>{children}</Box>,
      em: ({ children }: any) => <Box component="em" sx={{ fontStyle: 'italic' }}>{children}</Box>,
      a: ({ href, children }: any) => (
          <Box component="a" href={href} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
              {children}
          </Box>
      ),
      code: ({ inline, children }: any) => inline
          ? <Box component="code" sx={{ px: 0.5, py: 0.125, borderRadius: 0.75, bgcolor: 'action.hover', fontFamily: 'monospace', fontSize: '0.85em' }}>{children}</Box>
          : <Box component="pre" sx={{ p: 1.25, my: 1, borderRadius: 1, bgcolor: 'action.hover', overflowX: 'auto', fontFamily: 'monospace', fontSize: '0.85em' }}><code>{children}</code></Box>,
      h1: ({ children }: any) => <Typography variant="h6" sx={{ fontWeight: 600, mt: 1.5, mb: 0.75 }}>{children}</Typography>,
      h2: ({ children }: any) => <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 1.25, mb: 0.5 }}>{children}</Typography>,
      h3: ({ children }: any) => <Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 1, mb: 0.5 }}>{children}</Typography>,
  };

  return (
    <>
      <Stack direction="column" alignItems="center" sx={{ width: "100%", height: "100%", boxSizing: "border-box", bgcolor: 'background.default', pb: theme.spacing(1.5) }}>
      <Stack direction="column" spacing={2.5} sx={{ width: "100%", maxWidth: '820px', overflowY: "auto", flexGrow: 1, px: { xs: 2, sm: 3 }, pt: { xs: 2, sm: 3 }, pb: 2 }}>
          {chatMessages.length === 0 && !isLoading && (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', py: 8, color: 'text.secondary' }}>
                  <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5 }}>
                      How can I help?
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                      {subjectDisplayName
                          ? <>Ask anything about <Box component="span" sx={{ color: 'text.primary', fontWeight: 500 }}>{subjectDisplayName}</Box> to get started.</>
                          : 'Ask a question to get started.'}
                  </Typography>
              </Box>
          )}
          {chatMessages.map((message, index) => {
              if (message.isBot) {
                  const canFeedback = index !== 0;
                  const alreadySubmitted = submittedFeedback.includes(index);
                  const isCopied = copiedIndex === index;
                  return (
                      <Box key={index} sx={{ alignSelf: 'stretch', width: '100%' }}>
                          <Box sx={{ color: 'text.primary' }}>
                              <ReactMarkdown components={markdownComponents} rehypePlugins={[rehypeRaw]}>
                                  {message.text}
                              </ReactMarkdown>
                          </Box>
                          {canFeedback && (
                              <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5, ml: -0.75 }}>
                                  {alreadySubmitted ? (
                                      <Typography variant="caption" sx={{ pl: 1, color: 'success.main', fontSize: '0.72rem' }}>
                                          Feedback logged
                                      </Typography>
                                  ) : (
                                      <>
                                          <Tooltip title="Good response">
                                              <IconButton size="small" onClick={() => handleOpenFeedbackDialog(index, 5)} sx={{ color: 'text.secondary' }}>
                                                  <ThumbUpOutlinedIcon sx={{ fontSize: '1rem' }} />
                                              </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Bad response">
                                              <IconButton size="small" onClick={() => handleOpenFeedbackDialog(index, 1)} sx={{ color: 'text.secondary' }}>
                                                  <ThumbDownOutlinedIcon sx={{ fontSize: '1rem' }} />
                                              </IconButton>
                                          </Tooltip>
                                      </>
                                  )}
                                  <Tooltip title={isCopied ? "Copied" : "Copy"}>
                                      <IconButton size="small" onClick={() => handleCopyMessage(index, message.text)} sx={{ color: isCopied ? 'success.main' : 'text.secondary' }}>
                                          {isCopied
                                              ? <CheckIcon sx={{ fontSize: '1rem' }} />
                                              : <ContentCopyOutlinedIcon sx={{ fontSize: '1rem' }} />}
                                      </IconButton>
                                  </Tooltip>
                              </Stack>
                          )}
                      </Box>
                  );
              }
              return (
                  <Box key={index} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Typography
                          variant="body2"
                          sx={{
                              px: 2, py: 1.25,
                              borderRadius: '20px',
                              bgcolor: 'action.hover',
                              color: 'text.primary',
                              maxWidth: { xs: '85%', sm: '75%' },
                              wordBreak: 'break-word',
                              fontSize: isSmallScreen ? '0.9rem' : '0.95rem',
                              lineHeight: 1.5,
                              whiteSpace: 'pre-wrap',
                          }}
                      >
                          {message.text}
                      </Typography>
                  </Box>
              );
          })}
          {isLoading && (
              <Box sx={{ alignSelf: 'stretch', width: '100%' }}>
                  <Typography variant="body2" sx={{ fontSize: isSmallScreen ? '0.9rem' : '0.95rem', color: 'text.secondary', fontStyle: 'italic', display: 'inline-flex', alignItems: 'baseline' }}>
                      Thinking<span className="dots-container"></span>
                  </Typography>
              </Box>
          )}
          <div ref={messagesEndRef} />
      </Stack>

      <Dialog open={openFeedbackDialog} onClose={handleCloseFeedbackDialog} fullWidth maxWidth="xs">
        <DialogTitle sx={{ pb: 1, fontSize: '1.1rem' }}>Provide Feedback</DialogTitle>
        <DialogContent sx={{ pt: '4px !important' }}>
          <Typography component="legend" sx={{ mb: 0.5, fontSize: '0.9rem' }}>Rate the answer:</Typography>
          <Rating value={currentRating} onChange={(_, newValue) => setCurrentRating(newValue)} precision={1} />
          <TextField label="Additional Feedback (Optional)" multiline rows={3} value={currentFeedbackText} onChange={(e) => setCurrentFeedbackText(e.target.value)} variant="outlined" size="small" sx={{ marginTop: 1.5, width: "100%", fontSize: '0.9rem' }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseFeedbackDialog} size="small">Cancel</Button>
          <Button variant="contained" onClick={handleSubmitFeedback} disabled={currentRating === null || isSubmittingFeedback} size="small">
            {isSubmittingFeedback ? "Submitting..." : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>

      <Stack sx={{ width: '100%', maxWidth: '960px', flexShrink: 0, mb: 1, minHeight: '48px' }}>
          {isFeedbackSuccess && (
            <Alert severity="success" sx={{ width: "100%", boxSizing: 'border-box', fontSize: '0.8rem', py: 0.5 }}>
              Feedback logged successfully.
            </Alert>
          )}
          {(apiError || isFeedbackError) && (
            <Alert severity="error" sx={{ width: "100%", boxSizing: 'border-box', fontSize: '0.8rem', py: 0.5 }}>
              Unable to reach the server. Please check connection.
            </Alert>
          )}
      </Stack>

      <Stack sx={{ width: '100%', alignItems: 'center', flexShrink: 0, px: { xs: 2, sm: 3 }, pb: 1 }}>
        <Box component="form" onSubmit={handleSubmit} noValidate autoComplete="off" sx={{ width: '100%', maxWidth: '820px' }}>
          <Paper
              elevation={0}
              sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  p: filterOptions ? '6px 6px 6px 6px' : '6px 6px 6px 16px',
                  borderRadius: '24px',
                  bgcolor: 'action.hover',
                  border: '1px solid',
                  borderColor: 'divider',
                  transition: 'border-color 120ms ease',
                  '&:focus-within': { borderColor: 'primary.main' },
              }}
          >
              {filterOptions && filterOptions.length > 0 && (
                  <Tooltip title={filterLabel || 'Filter'}>
                      <IconButton
                          size="small"
                          onClick={(e: React.MouseEvent<HTMLButtonElement>) => setFilterAnchor(e.currentTarget)}
                          sx={{
                              alignSelf: 'center',
                              mb: 0.25,
                              color: filterValue && filterValue !== 'All' ? 'primary.main' : 'text.secondary',
                          }}
                          aria-label={filterLabel || 'Filter'}
                      >
                          <FilterAltOutlinedIcon fontSize="small" />
                      </IconButton>
                  </Tooltip>
              )}
              {filterValue && filterValue !== 'All' && (
                  <Chip
                      size="small"
                      label={filterValue}
                      onDelete={onFilterChange ? () => onFilterChange(filterOptions && filterOptions.includes('All') ? 'All' : null) : undefined}
                      sx={{
                          alignSelf: 'center',
                          mr: 0.5,
                          height: 24,
                          fontSize: '0.75rem',
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          '& .MuiChip-deleteIcon': { color: 'primary.contrastText', opacity: 0.8, '&:hover': { opacity: 1, color: 'primary.contrastText' } },
                      }}
                  />
              )}
              <InputBase
                  id="prompt-input"
                  sx={{ flex: 1, fontSize: isSmallScreen ? '0.9rem' : '0.95rem', py: 0.75, pl: filterOptions ? 0.5 : 0, lineHeight: 1.5 }}
                  placeholder="Message the assistant…"
                  multiline
                  maxRows={6}
                  value={prompt}
                  onChange={handleInputChange}
                  onKeyDown={handleInputKeyDown}
                  disabled={isLoading}
              />
              <IconButton
                  type="submit"
                  disabled={isLoading || !prompt.trim()}
                  size="small"
                  aria-label="Send message"
                  sx={{
                      p: 1,
                      ml: 0.5,
                      bgcolor: prompt.trim() && !isLoading ? 'primary.main' : 'action.disabledBackground',
                      color: 'white',
                      '&:hover': { bgcolor: prompt.trim() && !isLoading ? 'primary.dark' : 'action.disabledBackground' },
                      '&.Mui-disabled': { color: 'rgba(255,255,255,0.6)', bgcolor: 'action.disabledBackground' },
                  }}
              >
                  {isLoading
                      ? <CircularProgress size={18} sx={{ color: 'inherit' }} />
                      : <SendIcon fontSize="small" />}
              </IconButton>
          </Paper>
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 0.75, color: 'text.disabled', fontSize: '0.7rem' }}>
              Press Enter to send · Shift+Enter for newline
          </Typography>
        </Box>
      </Stack>
      </Stack>

      {filterOptions && (
          <Popover
              open={Boolean(filterAnchor)}
              anchorEl={filterAnchor}
              onClose={() => setFilterAnchor(null)}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              slotProps={{ paper: { sx: { borderRadius: 2, mt: -1, width: 300 } } }}
          >
              <Box sx={{ p: 1.5 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.08em', color: 'text.disabled', textTransform: 'uppercase', mb: 0.75 }}>
                      {filterLabel || 'Filter'}
                  </Typography>
                  <Autocomplete
                      options={filterOptions}
                      value={filterValue || null}
                      onChange={(_e: any, v: string | null) => onFilterChange?.(v)}
                      fullWidth
                      size="small"
                      autoHighlight
                      openOnFocus
                      renderInput={(params: any) => (
                          <TextField
                              {...params}
                              autoFocus
                              variant="outlined"
                              placeholder={`Select a ${(filterPlaceholder || 'value').toLowerCase()}…`}
                              InputProps={{
                                  ...params.InputProps,
                                  sx: { fontSize: isSmallScreen ? '0.85rem' : '0.9rem', borderRadius: 2 },
                              }}
                          />
                      )}
                  />
              </Box>
          </Popover>
      )}

      {/* --- FLOATING EXPORT BUTTON --- */}
      {showExportButton && (
          <Tooltip title="Export conversation">
              <Box sx={{
                  position: 'fixed',
                  top: 72,
                  right: 16,
                  zIndex: (theme) => theme.zIndex.speedDial
              }}>
                  <Fab
                      size="small"
                      color="primary"
                      aria-label="export conversation"
                      onClick={handleOpenExportDialog}
                      className={showPulse ? 'pulse-animation-once' : ''}
                  >
                      <IosShareIcon />
                  </Fab>
              </Box>
          </Tooltip>
      )}

      {/* --- EXPORT DIALOG / OVERLAY --- */}
      <Dialog open={isExportDialogOpen} onClose={handleCloseExportDialog} fullWidth maxWidth="sm" aria-labelledby="export-dialog-title">
          <DialogTitle sx={{ m: 0, p: 2, position: 'relative' }} id="export-dialog-title">
              Export conversation
              <IconButton
                  aria-label="Close export dialog"
                  onClick={handleCloseExportDialog}
                  disabled={isExporting}
                  sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
              >
                  <CloseIcon />
              </IconButton>
              {isExporting && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%' }} />}
          </DialogTitle>
          <DialogContent dividers>
              <DialogContentText sx={{ mb: 2 }}>
                  Choose what to include. You can export the full thread or just a subset.
              </DialogContentText>
              <fieldset disabled={isExporting} style={{ border: 'none', padding: 0, margin: 0 }}>
                  <FormControl component="div">
                      <FormLabel component="legend">Scope</FormLabel>
                      <RadioGroup row value={exportScope} onChange={(e) => setExportScope(e.target.value as 'all' | 'select')}>
                          <FormControlLabel value="all" control={<Radio />} label="All Q&A pairs" />
                          <FormControlLabel value="select" control={<Radio />} label="Select Q&A pairs" />
                      </RadioGroup>
                  </FormControl>
                  {exportScope === 'select' && (
                    <Paper variant="outlined" sx={{ mt: 2, maxHeight: '40vh', overflowY: 'auto' }}>
                      <List dense>
                          <ListItem button onClick={(e) => handleToggleSelectAll({ target: { checked: !areAllPairsSelected } } as any)}>
                              <ListItemIcon><Checkbox edge="start" checked={areAllPairsSelected} indeterminate={areSomePairsSelected} /></ListItemIcon>
                              <ListItemText primary={<b>Select All</b>} />
                          </ListItem>
                          {qaPairsForSelection.map((pair) => (
                              <ListItem key={pair.questionIndex} button onClick={() => handleTogglePairSelection(pair.questionIndex)}>
                                  <ListItemIcon><Checkbox edge="start" checked={selectedPairs.includes(pair.questionIndex)} tabIndex={-1} /></ListItemIcon>
                                  <ListItemText 
                                      primaryTypographyProps={{ noWrap: true, textOverflow: 'ellipsis' }}
                                      secondaryTypographyProps={{ noWrap: true, textOverflow: 'ellipsis' }}
                                      primary={`Q: ${pair.question}`} 
                                      secondary={`A: ${pair.answer}`} 
                                  />
                              </ListItem>
                          ))}
                      </List>
                    </Paper>
                  )}
              </fieldset>
          </DialogContent>
          <DialogActions>
              <Button onClick={handleCloseExportDialog} disabled={isExporting}>Cancel</Button>
              <Button
                  onClick={handleExport}
                  disabled={isExporting || (exportScope === 'select' && selectedPairs.length === 0)}
                  variant="contained"
                  startIcon={isExporting ? <CircularProgress size={20} color="inherit" /> : null}
              >
                  {isExporting ? "Preparing…" : "Export"}
              </Button>
          </DialogActions>
      </Dialog>
    </>
  );
};

export default ChatPrompt;